/**
 * Срез пинов подборки (чистая логика): период → вид → слово → библиотека → тег → сортировка.
 * Число у каждой фишки считается по срезу ДО её фильтра; мемоизация — в компоненте.
 */
import type { Pin } from "../../../lib/trends";

export type KindFilter = "all" | "image" | "video";
export type Period = "all" | "year" | "quarter" | "month" | "week";
export type SortKey = "similar" | "trend" | "saves" | "reactions" | "repins" | "shares" | "fresh";
export type TagFilter = number | "none" | null;
export type LibraryFilter = "all" | "in" | "out";

export const KINDS: readonly KindFilter[] = ["all", "image", "video"];
export const LIBRARIES: readonly LibraryFilter[] = ["all", "in", "out"];
export const PERIODS: readonly Period[] = ["all", "year", "quarter", "month", "week"];

export const PERIOD_DAYS: Record<Period, number | null> = { all: null, year: 365, quarter: 90, month: 30, week: 7 };

/** Числовой ключ сортировки: `null` — в хвост, при равенстве — по сохранениям. */
export const SORT_KEY: Record<SortKey, (p: Pin) => number | null> = {
  similar: (p) => p.similarity ?? null,
  trend: (p) => p.speed,
  saves: (p) => p.saves,
  reactions: (p) => p.reactions,
  repins: (p) => p.repins,
  shares: (p) => p.shares,
  fresh: (p) => (p.age_days === null ? null : -p.age_days),
};

export function sortPins(pins: Pin[], sort: SortKey): Pin[] {
  const key = SORT_KEY[sort];
  return [...pins].sort((a, b) => {
    const x = key(a);
    const y = key(b);
    if (x !== y) {
      if (x === null) return 1;
      if (y === null) return -1;
      return y - x;
    }
    return (b.saves ?? -1) - (a.saves ?? -1);
  });
}

/** Сколько пинов несут каждое значение (слово, тег). */
export function countBy(pins: Pin[], values: (p: Pin) => number[]): Map<number, number> {
  const out = new Map<number, number>();
  for (const p of pins) for (const v of values(p)) out.set(v, (out.get(v) ?? 0) + 1);
  return out;
}

export function inPeriod(pins: Pin[], period: Period): Pin[] {
  const days = PERIOD_DAYS[period];
  return days === null ? pins : pins.filter((p) => p.age_days !== null && p.age_days <= days);
}

export function kindCounts(pins: Pin[]): { all: number; image: number; video: number } {
  return {
    all: pins.length,
    image: pins.filter((p) => p.kind === "image").length,
    video: pins.filter((p) => p.kind === "video").length,
  };
}

export function byKind(pins: Pin[], kind: KindFilter): Pin[] {
  return kind === "all" ? pins : pins.filter((p) => p.kind === kind);
}

/** Слово — только в подборке (`topic`) и только если выбрано. */
export function byWord(pins: Pin[], topic: boolean, wordFilter: number | null): Pin[] {
  return topic && wordFilter !== null ? pins.filter((p) => p.type_ids?.includes(wordFilter)) : pins;
}

export function byLibrary(pins: Pin[], libraryFilter: LibraryFilter): Pin[] {
  return libraryFilter === "all" ? pins : pins.filter((p) => (p.in_libraries > 0) === (libraryFilter === "in"));
}

/** Последний шаг: тег, затем сортировка. */
export function slicePins(pins: Pin[], tagFilter: TagFilter, sort: SortKey): Pin[] {
  const byTag =
    tagFilter === null
      ? pins
      : tagFilter === "none"
        ? pins.filter((p) => p.tag_ids.length === 0)
        : pins.filter((p) => p.tag_ids.includes(tagFilter));
  return sortPins(byTag, sort);
}
